import { dateToTimestamp, parseAsDate } from "@/shared/utils/date";
import type { Currency } from "@/shared/value-objects/currency.vo";
import { Money } from "@/shared/value-objects/money.vo";
import {
  ControlResultColumnResponseDtoType,
  type ControlResultListItemResponseDto,
} from "@/spz-api/model";
import { DateFilter } from "@spz/ui/components/date-filter";
import { NumberFilter } from "@spz/ui/components/number-filter";
import { TextFilter } from "@spz/ui/components/text-filter";
import type { Column } from "@tanstack/react-table";
import { useControlResultFiltersStore } from "../store/control-result-filters.store";
import type { MonetaryFilterValueItem } from "../utils/filter-parser/values/monetary-filter-value";

type ControlResultDynamicFilterPopoverProps = {
  column: Column<ControlResultListItemResponseDto>;
  label: string;
  type: string;
  /**
   * currency use to filter - today only minorUnit is really necessary
   */
  currency?: Currency;
};

export const ControlResultDynamicFilterPopover = ({
  column,
  label,
  type,
  currency,
}: ControlResultDynamicFilterPopoverProps) => {
  const { columnFilters } = useControlResultFiltersStore();
  const value = columnFilters.find((f) => f.id === column.id)?.value;

  const getFilterValue = (index: number) => {
    return Array.isArray(value) ? value[index] : value;
  };

  const onNumberFilterChange = (min?: number, max?: number) => {
    column.setFilterValue([min, max]);
  };

  /**
   * Retrieve display amount in unit
   * @param index
   * @returns
   */
  const getMonetaryFilterValue = (index: number) => {
    const value = getFilterValue(index) as MonetaryFilterValueItem;

    if (!currency) {
      return undefined;
    }
    if (!value) {
      return undefined;
    }

    const monetaryValue = Money.create(value, currency);

    return monetaryValue.amountUnit;
  };

  /**
   * Store value amount
   * @param _min
   * @param _max
   * @returns
   */
  const onMonetaryFilterChange = (_min?: number, _max?: number) => {
    if (!currency) {
      return;
    }

    const isNumber = (value: unknown): value is number =>
      typeof value === "number";

    const min = isNumber(_min) ? Money.createByUnit(_min, currency) : undefined;
    const max = isNumber(_max) ? Money.createByUnit(_max, currency) : undefined;

    column.setFilterValue([min?.amount, max?.amount]);
  };

  const onDateFilterChange = (from?: Date, to?: Date) => {
    column.setFilterValue([
      from ? String(dateToTimestamp(from)) : "",
      to ? String(dateToTimestamp(to)) : "",
    ]);
  };

  const onTextFilterChange = (text: string) => column.setFilterValue(text);
  const onReset = () => column.setFilterValue(undefined);

  switch (type.toLowerCase()) {
    case ControlResultColumnResponseDtoType.INTEGER.toLowerCase():
      return (
        <NumberFilter
          label={label}
          onApply={onNumberFilterChange}
          onReset={onReset}
          min={getFilterValue(0)}
          max={getFilterValue(1)}
        />
      );

    case ControlResultColumnResponseDtoType.MONETARY.toLowerCase():
      return (
        <NumberFilter
          label={label}
          onApply={onMonetaryFilterChange}
          onReset={onReset}
          min={getMonetaryFilterValue(0)}
          max={getMonetaryFilterValue(1)}
          disabled={!currency}
        />
      );

    case ControlResultColumnResponseDtoType.TEXT.toLowerCase():
      return (
        <TextFilter
          label={label}
          value={getFilterValue(0) ?? ""}
          onChange={(e) => onTextFilterChange(e.target.value)}
        />
      );

    case ControlResultColumnResponseDtoType.SHORT_DATE.toLowerCase():
      return (
        <DateFilter
          label={label}
          from={getFilterValue(0) ? parseAsDate(getFilterValue(0)) : undefined}
          to={getFilterValue(1) ? parseAsDate(getFilterValue(1)) : undefined}
          onApply={onDateFilterChange}
        />
      );

    default:
      return null;
  }
};
